import { Bell, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from '@clareefai/ui';
import {
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
} from '../../../store/notificationApi';

export default function TeamNotificationsTab() {
  const { t } = useTranslation();
  const { data: response, isLoading } = useGetNotificationSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] = useUpdateNotificationSettingsMutation();

  const handleToggle = async (key: string, value: boolean) => {
    const newSettings = { ...(response?.settings ?? {}), [key]: value };
    try {
      await updateSettings({ settings: newSettings }).unwrap();
    } catch {
      toast.error(t('teamSettings.notifications.updateError'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-purple-600" />
      </div>
    );
  }

  const settings = response?.settings ?? {};
  const keys = Object.keys(settings).sort();

  if (keys.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Bell className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        <p>{t('teamSettings.notifications.empty')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Bell className="w-5 h-5 text-purple-500" />
        <div>
          <h2 className="text-lg font-bold text-gray-900">{t('teamSettings.notifications.heading')}</h2>
          <p className="text-sm text-gray-500">{t('teamSettings.notifications.intro')}</p>
        </div>
      </div>

      <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
        {keys.map((key) => {
          const enabled = settings[key];
          const label = t(`teamSettings.notifications.types.${key}.label`, {
            defaultValue: key.replace(/_/g, ' '),
          });
          const description = t(`teamSettings.notifications.types.${key}.description`, {
            defaultValue: '',
          });
          return (
            <div key={key} className="flex items-center justify-between px-4 py-4 bg-white">
              <div className="min-w-0 mr-4">
                <p className="text-sm font-medium text-gray-900">{label}</p>
                {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
              </div>
              <button
                onClick={() => handleToggle(key, !enabled)}
                disabled={isUpdating}
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                  enabled ? 'bg-purple-600' : 'bg-gray-300'
                } disabled:opacity-50`}
                role="switch"
                aria-checked={enabled}
              >
                <div
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${
                    enabled ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
